import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Dict } from '@se/se.ui.html5';
import { ColumnState, GridApi } from 'ag-grid-community';
import { GridUserPreference, RadioSettings } from 'estensa-module';
import { entries, find, forEach, isEmpty, isNil } from 'lodash-es';
import {
    EstensaColDef,
    EstensaGridSetting,
    EstensaSettingsType,
    GridState,
    SearchGridConfiguration,
    SearchParam,
    SearchToolbarConfiguration,
    SearchToolbarState,
    SearchTypeInfo,
    SettingDetail,
    ToolbarDef,
    ToolbarState,
} from './search-param.model';
import { SearchFormType } from './search-page-type.enum';
import { AdminPagesUserPreference, UserConfiguration, UserPreferences } from '../models/estensa-models.model';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { RootState } from '../root-store/root.state';
import { seSearchTypeChange } from './redux/search-page.actions';
import { GridUtilitiesService } from '../services/grid-utilities.service';
import { specialtyColumnDefs } from './search-page-column-defs/specialty-column.defs';
import { executionColumnDefs, primaryExecutionColumnDef } from './search-page-column-defs/execution-column.defs';
import { visitColumnDefs } from './search-page-column-defs/visit-column.defs';
import { ecgColumnDefs } from './search-page-column-defs/ecg-column.defs';
import { completeCdColumnDefs } from './search-page-column-defs/complete-cd-column.defs';
import { onPrintingCdColumnDefs } from './search-page-column-defs/on-printing-cd-column.defs';
import { nreColumnDefs } from './search-page-column-defs/nre-column.defs';
import { telemonitoringColumnDefs } from './search-page-column-defs/telemonitoring-column.defs';
import { executionToolbarDef } from './search-page-toolbar-defs/execution-toolbar.defs';
import { Observable, Subject } from 'rxjs';
import { executionUserFilterDefs } from './search-page-filter-defs/execution-filter.defs';

@Injectable({
    providedIn: 'root',
})
export class SearchPageConfigurationService {
    private searchTypes: { [K in SearchFormType]?: SearchTypeInfo[] } = {};
    private gridConfigurations: Dict<SearchGridConfiguration> = {};
    private actualGridState: Dict<GridState> = {};
    private toolbarConfiguration: Dict<SearchToolbarConfiguration> = {};
    private columnDefs: Dict<EstensaColDef[]> = {};
    private allColumnsDef: Dict<EstensaColDef[]> = {};
    private toolbarDefs: Dict<ToolbarDef[]> = {};
    private actualToolbarState: Dict<SearchToolbarState> = {};
    private isRightPanelVisibleOnStartDic: Dict<boolean> = {};
    primaryCols: EstensaColDef[] = [];
    private filterDefs: Dict<SearchParam[]> = {};

    /*serve per comunicare a tutti i componenti interessati il cambio di stato primary in una colonna della worklist
     * l'informazione risiede comunque nello stato delle colonne manteunto da questo servizio
     */
    toggleColumnPrimary$: Subject<{ colId: string; primary: boolean }> = new Subject<{
        colId: string;
        primary: boolean;
    }>();

    toggleColumnVisibility$: Subject<{ colId: string; isVisible: boolean }> = new Subject<{
        colId: string;
        isVisible: boolean;
    }>();

    // constructor
    constructor(
        private translate: TranslateService,
        private gridUtils: GridUtilitiesService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        private radioSettingsService: RadioSettingsService,
        private store: Store<RootState>,
    ) {
        this.initService();
    }

    setColDefPrimary(formType: SearchFormType, colId: string, primary: boolean): void {
        this.allColumnsDef[formType].map((colDef: EstensaColDef) => {
            if (colDef.colId === colId && colDef.estensaDetails) {
                colDef.estensaDetails.primary = primary;
            }
        });

        this.toggleColumnPrimary$.next({ colId, primary });
    }

    updateGrid(gridApi: GridApi, formType: SearchFormType, settingsParam: SettingDetail[], colState: ColumnState[]) {
        this.updateFromSettings(settingsParam, formType);
        this.updateGridPrimary(gridApi, formType);
        gridApi.applyColumnState({ state: colState, applyOrder: true });
    }

    updateFromSettings(settingsParam: SettingDetail[], formType: SearchFormType) {
        for (let colDef of this.allColumnsDef[formType]) this.setSettings(colDef, settingsParam);
    }

    updateGridPrimary(gridApi: GridApi, formType: SearchFormType) {
        if (!isNil(gridApi)) {
            this.primaryCols = this.allColumnsDef[formType].filter((el) => el.estensaDetails?.primary);
            this.columnDefs[formType] = this.primaryCols;
            gridApi.updateGridOptions({ columnDefs: this.primaryCols });
        }
    }

    private setSettings(colDef: EstensaColDef, settingsParam: SettingDetail[]) {
        for (let setting of settingsParam) {
            if (colDef.colId == setting.colId && colDef.estensaDetails) {
                colDef.estensaDetails.primary = setting.estensa.primary;
                colDef.hide = setting.hide;
            }
        }
    }

    //serve a rendere primarie o no le colonne icona sulla base del dato presente nella worklist
    //handlePrimaryColsFromValueList(items: any[], formType: SearchFormType, gridApi: GridApi) {
    //    switch (formType) {
    //        case SearchFormType.EXECUTION: {
    //            primaryByFieldValue(items, this.allColumnsDef[formType], formType);
    //            this.updateGridPrimary(gridApi, formType);
    //            const colState = gridApi.getColumnState();
    //            gridApi.applyColumnState({ state: colState, applyOrder: true });
    //        }
    //            break;
    //        default:
    //    }
    //}

    setColDefVisibility(formType: SearchFormType, colId: string, isVisible: boolean): void {
        const colDef = this.allColumnsDef[formType].find((colDef: EstensaColDef) => colDef.colId === colId);
        if (colDef) {
            colDef.hide = !isVisible;
        }
        this.toggleColumnVisibility$.next({ colId, isVisible });
    }

    getMultiSearchEnabled(formType: SearchFormType): boolean {
        return !isEmpty(this.searchTypes[formType]);
    }

    getSearchTypes(form: SearchFormType): SearchTypeInfo[] {
        return this.searchTypes[form] ?? [];
    }

    getDefaultConfigurationUserPreferences(): Observable<UserPreferences> {
        return this.userConfigurationSettingsService.getDefaultConfigurationUserPreferences();
    }

    // #region GRID SETTINGS

    /**
     * Ritorna il column state per la form specificata: se esiste una versione 'actual' usa quella e la annulla,
     * viceversa restituisce l'ultima salvata da interfaccia (se esiste)
     * @param form
     */
    getColumnState(form: SearchFormType) {
        const actualState = this.gridConfigurations[form].actualColState;
        if (!!actualState && actualState.length > 0) {
            let t = this.gridConfigurations[form].actualColState;
            this.gridConfigurations[form].actualColState = null;
            return t;
        }

        return this.gridConfigurations[form].colState;
    }

    getFactoryColumnState(form: SearchFormType) {
        return this.gridConfigurations[form].factoryColState;
    }

    getColumnDefs(form: SearchFormType) {
        return this.columnDefs[form];
    }

    getAllColumnDefs(form: SearchFormType) {
        if (!this.allColumnsDef[form]) return [];

        return this.allColumnsDef[form];
    }

    getDefaultColumnDefs(form: SearchFormType) {
        switch (form) {
            case SearchFormType.EXECUTION:
                return executionColumnDefs(this.translate, this.gridUtils);
            default: //do nothing
        }
    }

    getIsRightPanelVisibleOnStart(form: SearchFormType): boolean {
        return this.isRightPanelVisibleOnStartDic[form];
    }

    storeGridFactoryState(factoryColState: ColumnState[], form: SearchFormType) {
        this.gridConfigurations[form].factoryColState = factoryColState.map((col, i) => ({
            ...col,
            colId: col.colId ?? `col_${i}`,
        }));
    }

    // #region new methods grid and save/store settings
    saveGridFactoryStatus(factoryColState: ColumnState[], columnDef: EstensaColDef[], form: SearchFormType) {
        this.gridConfigurations[form].factoryColState = factoryColState.map((col, i) => ({
            ...col,
            colId: col.colId ?? `col_${i}`,
        }));
        this.gridConfigurations[form].factoryColumnDefs = columnDef.map((col, i) => ({
            ...col,
            colId: col.colId ?? `col_${i}`,
        }));
    }

    getGridSettings(form: SearchFormType): EstensaGridSetting[] {
        const actualState = this.gridConfigurations[form].actualColState;
        if (!!actualState && actualState.length > 0) {
            this.gridConfigurations[form].actualColState = null;
            return [{ details: [], colsState: [] }];
        }

        //per seguire le impostazioni attuali è necessario castare il colState
        if (isNil(this.actualGridState[form].colState)) {
            const colsDefState: EstensaGridSetting[] = [{ details: [], colsState: [] }];
            return colsDefState;
        } else {
            let gridStateSettingSaved = this.actualGridState[form].colState as EstensaGridSetting;
            const colsDefState: EstensaGridSetting[] = [
                {
                    details: gridStateSettingSaved.details,
                    colsState: gridStateSettingSaved.colsState,
                },
            ];
            return colsDefState;
        }
    }

    // #endregion

    storeAndSaveFiltersState(
        filtersState: SearchParam[] /*FilterState[]*/,
        form: SearchFormType,
        settingsType: EstensaSettingsType,
    ) {
        let adminPref: AdminPagesUserPreference = {};

        const jsonFilterState = JSON.stringify(filtersState);

        switch (form) {
            case SearchFormType.EXECUTION:
                adminPref.execution = {
                    filters: jsonFilterState,
                };
                break;
            default:
                throw new Error('Filters configuration not supported for ' + form);
        }

        let preferences: UserPreferences = { adminPagesUserPreference: adminPref };

        settingsType == EstensaSettingsType.user
            ? this.userConfigurationSettingsService.setUserPreferences(preferences)
            : this.userConfigurationSettingsService.setDefaultConfigurationUserPreferences(preferences);
    }

    /**
     * Assegna i valori passati alla gridConfiguration e salva su settings
     */
    storeAndSaveGridState(
        colState: ColumnState[],
        form: SearchFormType,
        settingsType: EstensaSettingsType,
        param?: SettingDetail[],
    ) {
        this.gridConfigurations[form].colState = colState;

        /* SALVARE IL SETTING SU DB */
        const jsonColState = JSON.stringify(colState);

        let gridUserPref: Partial<GridUserPreference> | undefined;

        switch (form) {
            case SearchFormType.ECG:
                gridUserPref = {
                    ecgWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.VISIT:
                gridUserPref = {
                    visitWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.COMPLETE_CD:
                gridUserPref = {
                    completeCdWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.ON_PRINTING_CD:
                gridUserPref = {
                    onPrintingCdWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.TELEMONITORING:
                gridUserPref = {
                    telemonitoringWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.NRE:
                gridUserPref = {
                    nreWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.DUMMY_VNEXT2:
                gridUserPref = {
                    executionWorklistGrid: jsonColState,
                };
                break;
            case SearchFormType.EXECUTION:
                const settings: EstensaGridSetting = {
                    details: param ? param : [],
                    colsState: colState,
                };
                const jsonSettings = JSON.stringify(settings);
                gridUserPref = {
                    executionWorklistGrid: jsonSettings,
                };
                break;
            case SearchFormType.SPECIALTY:
                gridUserPref = {
                    specialtyWorklistGrid: jsonColState,
                };
                break;
            default:
            // Do nothing
        }

        let preferences: UserPreferences = {
            gridUserPreference: gridUserPref,
        };

        settingsType == EstensaSettingsType.user
            ? this.userConfigurationSettingsService.setUserPreferences(preferences)
            : this.userConfigurationSettingsService.setDefaultConfigurationUserPreferences(preferences);
    }

    storeGridActualState(colState: ColumnState[], form: SearchFormType) {
        this.gridConfigurations[form].actualColState = colState;
    }

    resetAllActualConfigurations() {
        forEach(this.gridConfigurations, (config) => {
            config.actualColState = null;
        });
    }

    // #endregion GRID SETTINGS

    private initService() {
        // Inizializzo le column definitions di fabbrica
        this.initColumnDefs();

        this.initToolbarDef();

        this.initFilterDef();

        // Lettura dei settings
        let settings: RadioSettings = this.radioSettingsService.radioSettings;
        const userSettings: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;

        /* LASCIATO PER FUTURI IMPORT */
        //settings.visitWorklistSettings.searchParamDefs = JSON.stringify(visitParams);
        //settings.visitWorklistSettings.searchTypeDefs = JSON.stringify(visitSearchTypes);

        // ECG
        let deserializedEcgSearchType = JSON.parse(settings.ecgWorklistSettings.searchTypeDefs);

        // VISIT
        let deserializedVisitSearchType = JSON.parse(settings.visitWorklistSettings.searchTypeDefs);

        // COMPLETE_CD
        let deserializedCompleteCdSearchType = JSON.parse(settings.completeCdWorklistSettings.searchTypeDefs);

        // ON_PRINTING_CD
        let deserializedOnPrintingCdSearchType = JSON.parse(settings.onPrintingCdWorklistSettings.searchTypeDefs);

        // TELEMONITORING - MANCANO I SETTINGS
        const telemonitoringSearchType =
            '[{ "id": 0, "labelTranslationKey": "SearchAll", "isDefaultSearch": true }, { "id": 1, "labelTranslationKey": "SearchActiveTelemonitoring", "isDefaultSearch": false }]';
        let deserializedTelemonitoringSearchType = JSON.parse(telemonitoringSearchType);

        //NRE - MANCANO I SETTINGS
        //const nreSearchType = '[{ "id": 0, "labelTranslationKey": "Search", "isDefaultSearch": true}]';
        //let deserializedNreSearchType = JSON.parse(nreSearchType);

        // EXECUTION (SETTING USER)
        const deserializedExecutionSearchType = JSON.parse(
            userSettings?.preferences.adminPagesUserPreference?.execution?.searchType ?? '[]',
        );

        // SPECIALTY
        const specialtySearchType =
            '[{ "id": 0, "labelTranslationKey": "SearchAll", "isDefaultSearch": true }, { "id": 1, "labelTranslationKey": "SearchActiveTelemonitoring", "isDefaultSearch": false }]';
        let deserializedSpecialtySearchType = JSON.parse(settings.specialtyWorklistSettings.searchTypeDefs || specialtySearchType);

        /* RIGHT PANEL VISIBILITY */
        this.isRightPanelVisibleOnStartDic[SearchFormType.ECG] =
            settings.ecgWorklistSettings.isRightPanelVisibleOnStart;
        this.isRightPanelVisibleOnStartDic[SearchFormType.VISIT] =
            settings.visitWorklistSettings.isRightPanelVisibleOnStart;
        this.isRightPanelVisibleOnStartDic[SearchFormType.COMPLETE_CD] = false;
        this.isRightPanelVisibleOnStartDic[SearchFormType.ON_PRINTING_CD] = false;
        this.isRightPanelVisibleOnStartDic[SearchFormType.TELEMONITORING] = true; // MANCANO I SETTINGS
        this.isRightPanelVisibleOnStartDic[SearchFormType.NRE] = true; // MANCANO I SETTINGS
        this.isRightPanelVisibleOnStartDic[SearchFormType.DUMMY_VNEXT2] = true;
        this.isRightPanelVisibleOnStartDic[SearchFormType.SPECIALTY] = Boolean(settings.specialtyWorklistSettings.isRightPanelVisibleOnStart);

        // EXECUTION (SETTING USER)
        this.isRightPanelVisibleOnStartDic[SearchFormType.EXECUTION] =
            userSettings?.preferences.adminPagesUserPreference?.execution?.isRightPanelVisibleOnStart ?? true;

        /* SEARCH TYPES */
        this.searchTypes[SearchFormType.ECG] = deserializedEcgSearchType;
        this.searchTypes[SearchFormType.VISIT] = deserializedVisitSearchType;
        this.searchTypes[SearchFormType.COMPLETE_CD] = deserializedCompleteCdSearchType;
        this.searchTypes[SearchFormType.ON_PRINTING_CD] = deserializedOnPrintingCdSearchType;
        this.searchTypes[SearchFormType.TELEMONITORING] = deserializedTelemonitoringSearchType; // MANCANO I SETTINGS
        /*  this.searchTypes[SearchFormType.NRE] = deserializedNreSearchType;*/ // MANCANO I SETTINGS*/
        this.searchTypes[SearchFormType.DUMMY_VNEXT2] = deserializedExecutionSearchType;
        //specialty
        this.searchTypes[SearchFormType.SPECIALTY] = deserializedSpecialtySearchType;
        this.searchTypes[SearchFormType.EXECUTION] = deserializedExecutionSearchType;

        // Assegna tutti i tipi di ricerca predefiniti.
        entries(this.searchTypes).forEach(([formType, searchTypes]) => {
            const defaultSearchType = find(searchTypes, (st) => st.isDefaultSearch);
            if (defaultSearchType) {
                this.store.dispatch(
                    seSearchTypeChange({
                        formType: formType as SearchFormType,
                        searchType: defaultSearchType as SearchTypeInfo,
                    }),
                );
            }
        });

        /* GRIGLIE */
        const config: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        if (config) {
            this.loadGridConfigurationFromSettings(config.preferences, 'ecgWorklistGrid', SearchFormType.ECG);
            this.loadGridConfigurationFromSettings(config.preferences, 'visitWorklistGrid', SearchFormType.VISIT);
            this.loadGridConfigurationFromSettings(
                config.preferences,
                'completeCdWorklistGrid',
                SearchFormType.COMPLETE_CD,
            );
            this.loadGridConfigurationFromSettings(
                config.preferences,
                'onPrintingCdWorklistGrid',
                SearchFormType.ON_PRINTING_CD,
            );
            this.loadGridConfigurationFromSettings(
                config.preferences,
                'telemonitoringWorklistGrid',
                SearchFormType.TELEMONITORING,
            );
            this.loadGridConfigurationFromSettings(config.preferences, 'nreWorklistGrid', SearchFormType.NRE);
            this.loadGridConfigurationFromSettings(
                config.preferences,
                'executionWorklistGrid',
                SearchFormType.DUMMY_VNEXT2,
            );
            this.loadGridSettings(config.preferences, 'executionWorklistGrid', SearchFormType.EXECUTION);
            this.loadGridConfigurationFromSettings(
                config.preferences,
                'specialtyWorklistGrid',
                SearchFormType.SPECIALTY,
            );
        }
        // Applico i columndefinitions e gli eventuali settings
        this.gridConfigurations[SearchFormType.ECG] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.ECG],
            this.actualGridState[SearchFormType.ECG],
        );
        this.gridConfigurations[SearchFormType.VISIT] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.VISIT],
            this.actualGridState[SearchFormType.VISIT],
        );
        this.gridConfigurations[SearchFormType.COMPLETE_CD] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.COMPLETE_CD],
            this.actualGridState[SearchFormType.COMPLETE_CD],
        );
        this.gridConfigurations[SearchFormType.ON_PRINTING_CD] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.ON_PRINTING_CD],
            this.actualGridState[SearchFormType.ON_PRINTING_CD],
        );
        this.gridConfigurations[SearchFormType.TELEMONITORING] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.TELEMONITORING],
            this.actualGridState[SearchFormType.TELEMONITORING],
        );
        this.gridConfigurations[SearchFormType.NRE] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.NRE],
            this.actualGridState[SearchFormType.NRE],
        );
        this.gridConfigurations[SearchFormType.DUMMY_VNEXT2] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.DUMMY_VNEXT2],
            this.actualGridState[SearchFormType.DUMMY_VNEXT2],
        );
        this.gridConfigurations[SearchFormType.EXECUTION] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.EXECUTION],
            this.actualGridState[SearchFormType.EXECUTION],
        );
        this.gridConfigurations[SearchFormType.SPECIALTY] = new SearchGridConfiguration(
            this.columnDefs[SearchFormType.SPECIALTY],
            this.actualGridState[SearchFormType.SPECIALTY],
        );

        /*TOOLBAR*/
        if (config) {
            this.loadToolbarConfigurationFromSettings(config.preferences, 'execution', SearchFormType.EXECUTION);
        }

        this.toolbarConfiguration[SearchFormType.EXECUTION] = new SearchToolbarConfiguration(
            this.toolbarDefs[SearchFormType.EXECUTION],
            this.actualToolbarState[SearchFormType.EXECUTION],
        );
    }

    initExecutionGrid() {
        const config: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        if (config) {
            this.loadGridSettings(config.preferences, 'executionWorklistGrid', SearchFormType.EXECUTION);
        }
    }

    initExecutionToolbar() {
        const config: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        if (config) {
            this.loadToolbarConfigurationFromSettings(config.preferences, 'execution', SearchFormType.EXECUTION);
            this.toolbarConfiguration[SearchFormType.EXECUTION] = new SearchToolbarConfiguration(
                this.toolbarDefs[SearchFormType.EXECUTION],
                this.actualToolbarState[SearchFormType.EXECUTION],
            );
        }
    }

    private initColumnDefs() {
        this.columnDefs[SearchFormType.ECG] = ecgColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.VISIT] = visitColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.COMPLETE_CD] = completeCdColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.ON_PRINTING_CD] = onPrintingCdColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.TELEMONITORING] = telemonitoringColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.NRE] = nreColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.DUMMY_VNEXT2] = visitColumnDefs(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.EXECUTION] = primaryExecutionColumnDef(this.translate, this.gridUtils);
        this.columnDefs[SearchFormType.SPECIALTY] = specialtyColumnDefs(this.translate, this.gridUtils);

        this.allColumnsDef[SearchFormType.EXECUTION] = executionColumnDefs(this.translate, this.gridUtils);
    }

    private initToolbarDef() {
        this.toolbarDefs[SearchFormType.EXECUTION] = executionToolbarDef(this.translate);
    }

    private initFilterDef() {
        this.filterDefs[SearchFormType.EXECUTION] = executionUserFilterDefs();
    }

    private loadGridConfigurationFromSettings(
        preferences: UserPreferences,
        gridSettingName: keyof GridUserPreference,
        formType: SearchFormType,
    ) {
        this.actualGridState[formType] = { colState: undefined };

        if (preferences?.gridUserPreference?.[gridSettingName]) {
            let colState = null;
            try {
                let p = preferences.gridUserPreference[gridSettingName].split('#GRIDSETTINGSSEPARATOR#');
                if (p[0]) colState = JSON.parse(p[0]);
            } catch {
                colState = null;
            }

            this.actualGridState[formType] = { colState: colState };
        }
    }

    // #region new Settings Load

    private loadGridSettings(
        preferences: UserPreferences,
        gridSettingName: keyof GridUserPreference,
        formType: SearchFormType,
    ) {
        this.actualGridState[formType] = { colState: undefined };
        if (preferences?.gridUserPreference?.[gridSettingName]) {
            let settings: EstensaGridSetting = { colsState: [], details: [] };
            try {
                let p = preferences.gridUserPreference[gridSettingName].split('#GRIDSETTINGSSEPARATOR#');
                if (p[0]) {
                    let settingParsed = JSON.parse(p[0]);
                    settings = {
                        colsState: settingParsed.colsState,
                        details: settingParsed.details,
                    };
                }
            } catch {
                settings = { colsState: [], details: [] };
            }
            this.actualGridState[formType] = { colState: settings };
        }
    }

    // #endregion

    // #region Toolbar

    private loadToolbarConfigurationFromSettings(
        preferences: UserPreferences,
        toolbarSettingName: keyof AdminPagesUserPreference,
        formType: SearchFormType,
    ) {
        this.actualToolbarState[formType] = { toolbarState: undefined };
        if (preferences.adminPagesUserPreference?.[toolbarSettingName]) {
            let toolbarState = null;
            try {
                let p = preferences.adminPagesUserPreference[toolbarSettingName];
                if (p && p.toolbar) {
                    toolbarState = JSON.parse(p.toolbar);
                }
            } catch {
                toolbarState = null;
            }
            this.actualToolbarState[formType] = { toolbarState: toolbarState };
        }
    }

    storeToolbarFactoryState(toolbarFactoryState: Array<ToolbarState>, form: SearchFormType) {
        this.toolbarConfiguration[form].factoryToolbarState = toolbarFactoryState.map((btn) => ({
            ...btn,
        }));
    }

    storeAndSaveToolbarState(toolbarState: ToolbarState[], form: SearchFormType, settingsType: EstensaSettingsType) {
        let adminPref: AdminPagesUserPreference = {};

        const jsonToolbarState = JSON.stringify(toolbarState);

        switch (form) {
            case SearchFormType.EXECUTION:
                adminPref.execution = {
                    toolbar: jsonToolbarState,
                };
                break;
            default:
                throw new Error('Toolbar configuration not supported for ' + form);
        }

        let preferences: UserPreferences = { adminPagesUserPreference: adminPref };

        settingsType == EstensaSettingsType.user
            ? this.userConfigurationSettingsService.setUserPreferences(preferences)
            : this.userConfigurationSettingsService.setDefaultConfigurationUserPreferences(preferences);
    }

    getToolbarState(form: SearchFormType): Array<ToolbarState> {
        if (
            !!this.toolbarConfiguration[form].actualToolbarState &&
            this.toolbarConfiguration[form].actualToolbarState.length > 0
        ) {
            let actual = this.toolbarConfiguration[form].actualToolbarState;
            this.toolbarConfiguration[form].actualToolbarState = [];
            return actual;
        }
        return this.toolbarConfiguration[form].toolbarState;
    }

    getToolbarStateFactory(form: SearchFormType): Array<ToolbarState> {
        return this.toolbarConfiguration[form].factoryToolbarState;
    }

    getToolbarDefs(form: SearchFormType) {
        return this.toolbarDefs[form];
    }

    // #endregion
}

// #endregion
